<template>
  <div class="form-holder q-ma-lg q-pl-xl q-pr-xl">
    <q-toolbar>
      <q-toolbar-title>{{ title }}</q-toolbar-title>
    </q-toolbar>

    <!-- 
      Instead of <q-row> and <q-col>,
      use Quasar’s row and col classes on standard <div> elements
    -->
    <q-form 
      @submit.prevent="handleFormSubmit"
      @reset="handleReset"
      ref="baseForm"
      @keydown.enter.prevent
    >
      <!-- Each field occupies its own 'col' in the row -->
      <div
        v-for="field in fields"
        :key="field.model"
        :class="[
          'col-12', // defaults to full width
          field.sm ? `col-sm-${field.sm}` : 'col-sm-6', // smaller width on SM
        ]"
      >
        <!-- Date Field -->
        <q-input
          v-if="field.type === 'datetime'"
          filled
          v-model="localFormData[field.model]"
          :label="field.label"
          :dense="true"
          :hint="resolveFieldHint(field)"
          :persistent-hint="!!resolveFieldHint(field)"
          :error="!!errors[field.model]"
          :error-message="errors[field.model]"
          :hide-bottom-space="!errors[field.model] && !resolveFieldHint(field)"
          class="q-mb-md"
          mask="####-##-##"
          :ref="field.refKey ? field.refKey : field.model"
          @focus="onDateFieldFocus(field)"
          @keydown.enter.prevent="handleEnterKey(field)"
        >
          <template v-slot:append>
            <q-icon name="event" class="cursor-pointer" color="primary">
              <q-popup-proxy :ref="`popup-${field.model}`" transition-show="scale" transition-hide="scale">
                <q-date
                  v-model="localFormData[field.model]"
                  mask="YYYY-MM-DD"
                  :options="dateRestriction || (() => true)"
                  :navigation-min-year-month="`${selectedYear.value}-01`"
                  :navigation-max-year-month="`${selectedYear.value}-12`"
                  @update:model-value="onDateSelected(field)"
                />
              </q-popup-proxy>
            </q-icon>
          </template>
        </q-input>

        <!-- Select Field -->
        <q-select
          v-else-if="field.type === 'select'"
          v-model="localFormData[field.model]"
          :options="typeof field.options === 'function'
            ? field.options()
            : field.options || selectOptions[field.model]"
          option-value="value"
          option-label="label"
          :label="field.label"
          :rules="field.rules"
          :hint="resolveFieldHint(field)"
          :persistent-hint="!!resolveFieldHint(field)"
          :error="!!errors[field.model]"
          :error-message="errors[field.model]"
          :dense="true"
          filled
          :hide-bottom-space="!errors[field.model] && !resolveFieldHint(field)"
          class="q-mb-md"
          emit-value
          map-options
          :ref="field.refKey ? field.refKey : field.model"
          @update:model-value="onSelectChanged(field)"
          @popup-show="onSelectPopupShow(field)"
          @popup-hide="onSelectPopupHide(field)"
        ></q-select>

        <!-- Default Input Field -->
        <q-input
          v-else
          v-model="localFormData[field.model]"
          :label="field.label"
          :rules="field.rules"
          :hint="resolveFieldHint(field)"
          :persistent-hint="!!resolveFieldHint(field)"
          :error="!!errors[field.model]"
          :error-message="errors[field.model]"
          :dense="true"
          filled
          :hide-bottom-space="!errors[field.model] && !resolveFieldHint(field)"
          class="q-mb-md"
          :ref="field.refKey ? field.refKey : field.model"
          @blur="handleFieldBlur(field)"
          @keydown.enter.prevent="handleEnterKey(field)"
        ></q-input>
      </div>

      <!-- Non-field errors -->
      <div v-if="nonFieldErrors" class="q-pa-md text-negative col-12">
        {{ nonFieldErrors }}
      </div>

      <!-- Buttons in their own 'col' -->
      <div class="col-12 col-sm-6">
        <q-btn
          ref="submitBtn"
          label="Submit"
          type="submit"
          color="primary"
          unelevated
          class="q-mt-md"
        />
        <q-btn label="Reset" type="reset" color="primary" flat class="q-ml-sm"></q-btn>
      </div>
    </q-form>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  props: {
    title: String,
    formData: Object,
    fields: Array,
    submitUrl: String,
    method: {
      type: String,
      default: 'post'
    },
    selectedYear: Number,
    dateRestriction: Function,
  },
  data() {
    return {
      localFormData: this.resetLocalFormData(), 
      selectOptions: {}, // Holds dynamically fetched options for select fields
      errors: {},
      nonFieldErrors: '',
      suppressNextDateFocusOpen: {},
    };
  },
  computed: {},
  watch: {
    formData: {
      handler(newVal) {
        this.localFormData = {...newVal};
      },
      deep: true
    },
    // watch `localFormData` for user edits, and emit a generic event:
    localFormData: {
      handler(newVal, oldVal) {
        this.$emit('form-changed', newVal, oldVal);
      },
      deep: true
    },
    selectedYear() {
      if (this.method === 'put') {
        return;
      }
      this.localFormData.date = null; 
    },
  },
  created() {
    this.fields.forEach(field => {
      if (field.type === 'select' && field.optionsUrl) {
        this.fetchSelectOptions(field.model, field.optionsUrl, field.labelField);
      }
    });
  },
  methods: {
    resolveFieldHint(field) {
      if (!field?.hint) {
        return '';
      }

      return typeof field.hint === 'function'
        ? field.hint(this.localFormData)
        : field.hint;
    },
    fetchSelectOptions(model, url, labelField) {
      axios.get(url).then(response => {
        this.selectOptions[model] = response.data.map(item => ({
          label: item[labelField],
          value: item.id
        }));
      }).catch(error => {
        console.error(`Failed to fetch options for ${model} from ${url}`, error);
      });
    },
    resetLocalFormData() {
      let tmp = {...this.formData};
      let rtrn = {...this.formData};
      if (this.method === 'post') {
        Object.keys(tmp).forEach(key => {
          tmp[key] = null;
        });
      }
      this.localFormData = tmp;
      return rtrn
    },
    openDatePopup(field) {
      const popupRef = this.$refs[`popup-${field.model}`];
      const popup = Array.isArray(popupRef) ? popupRef[0] : popupRef;
      popup?.show?.();
    },
    onDateFieldFocus(field) {
      if (!field.openDatePickerOnFocus) {
        return;
      }

      const currentValue = this.localFormData[field.model];
      const hasDateValue = currentValue !== null
        && currentValue !== undefined
        && String(currentValue).trim() !== '';
      if (hasDateValue) {
        return;
      }

      if (this.suppressNextDateFocusOpen[field.model]) {
        this.suppressNextDateFocusOpen[field.model] = false;
        return;
      }

      this.$nextTick(() => {
        this.openDatePopup(field);
      });
    },
    // Re-sync local form data from parent:
    refreshLocalFormData() {
      this.localFormData = { ...this.formData };
    },
    handleReset() {
      if (this.method === 'put') {
        this.localFormData = {...this.formData};
      } else {
        Object.keys(this.localFormData).forEach(key => {
          this.localFormData[key] = null;
        });
      }
      this.errors = {};
    },
    handleFormSubmit() {
      axios({
        method: this.method,
        url: this.submitUrl,
        data: this.localFormData,
        withCredentials: true
      }).then(response => {
        this.$emit('form-success', response);
        this.nonFieldErrors = '';
      }).catch(error => {
        this.$emit('form-error', error);
        console.log('YY', error);
        if (error.response && error.response.data) {
          const newErrors = {};
          if (error.response.data.non_field_errors) {
            this.nonFieldErrors = error.response.data.non_field_errors.join(', ');
          }
          Object.keys(error.response.data).forEach(key => {
            if (key !== 'non_field_errors') {
              newErrors[key] = error.response.data[key].join(', ');
            }
          });
          this.errors = newErrors;
        }
      });
    },
    focusField(fieldName) {
      const refArr = this.$refs[fieldName];
      if (refArr && refArr.length) {
        refArr[0].focus();
      }
    },
    handleEnterKey(currentField) {
      const currentIndex = this.fields.findIndex(f => f.model === currentField.model);
      if (currentIndex === -1) return;

      const nextField = this.fields[currentIndex + 1];
      if (!nextField) {
        // focus on submit button
        const submitBtn = this.$refs.submitBtn;

        if (submitBtn) {
          submitBtn.$el.focus();
        }
        return;
      }

      const refKey = nextField.refKey || nextField.model;
      let el = this.$refs[refKey];

      // Refs from v-for may return an array
      if (Array.isArray(el)) el = el[0];

      if (!el) return;

      // Quasar q-input / q-select should have a `.focus()` method
      if (typeof el.focus === 'function') {
        el.focus();
      } else if (el.$el?.focus) {
        el.$el.focus(); // fallback
      }

      // For q-select specifically, also open popup if desired
      if (typeof el.showPopup === 'function') {
        el.showPopup();
      }
    },
    handleFieldBlur(field) {
      this.$emit('field-blur', field, this.localFormData[field.model]);
    },
    onDateSelected(field) {
      this.suppressNextDateFocusOpen[field.model] = true;

      // Close popup for this field
      const popupRef = this.$refs[`popup-${field.model}`];
      if (Array.isArray(popupRef)) {
        popupRef[0]?.hide?.();
      } else {
        popupRef?.hide?.();
      }

      // Move to next field after date is picked
      this.$nextTick(() => {
        this.handleEnterKey(field);
      });
    },
    onSelectChanged(field) {
      // Delay ensures popup closes first before moving focus
      setTimeout(() => {
        this.handleEnterKey(field);
      }, 100);
    },
    onSelectPopupHide(field) {
    // Popup closed — only move focus if value didn't change
      const modelValue = this.localFormData[field.model];
      const initialValue = this.lastSelectValue;

      if (modelValue === initialValue) {
        setTimeout(() => this.handleEnterKey(field), 200);
      }
    },
    onSelectPopupShow(field) {
      this.lastSelectValue = this.localFormData[field.model];
    },
  }
};
</script>

<style scoped>
.form-holder {
  max-width: 700px;
}
</style>
